import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import api, { CHAINS, getExplorerAddressUrl, getErrorMessage, adjustSignatureForSafe } from '../../services/api';
import { Icons } from './DashboardLayout';
import { ethers } from 'ethers';

const STATUS_COLORS = {
  PROPOSED: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20', label: 'Pending' },
  PENDING: { bg: 'bg-yellow-500/10', text: 'text-yellow-400', border: 'border-yellow-500/20', label: 'Pending' },
  CONFIRMED: { bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20', label: 'Confirmed' },
  EXECUTED: { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20', label: 'Executed' },
  FAILED: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', label: 'Failed' },
  REJECTED: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20', label: 'Rejected' },
  CANCELLED: { bg: 'bg-white/5', text: 'text-white/40', border: 'border-white/10', label: 'Cancelled' },
};

export default function SafeSignPage() {
  const { agents, token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Safe address from URL or input
  const [safeAddress, setSafeAddress] = useState(searchParams.get('safe') || '');
  const [safeAddressInput, setSafeAddressInput] = useState(searchParams.get('safe') || '');
  const [chain, setChain] = useState(searchParams.get('chain') || 'base');
  
  // Safe data
  const [safeInfo, setSafeInfo] = useState(null);
  const [pendingTxs, setPendingTxs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Connected signer wallet (separate from auth)
  const [signerWallet, setSignerWallet] = useState(null);
  const [signerAddress, setSignerAddress] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [signingTx, setSigningTx] = useState(null);

  // Known Safes from user's agents (for quick select)
  const [knownSafes, setKnownSafes] = useState([]);

  // Load known Safes from user's agents
  useEffect(() => {
    if (agents && token) {
      loadKnownSafes();
    }
  }, [agents, token]);

  // Load Safe data when address changes
  useEffect(() => {
    if (safeAddress && safeAddress.length === 42) {
      loadSafeData();
      // Update URL
      setSearchParams({ safe: safeAddress, chain });
    }
  }, [safeAddress, chain]);

  const loadKnownSafes = async () => {
    const safes = [];
    for (const agent of agents) {
      try {
        const res = await api.getSafeInfo(token, agent.id);
        if (res.success && res.data) {
          const data = res.data;
          let safesList = [];
          if (Array.isArray(data)) {
            safesList = data;
          } else if (data.safes && Array.isArray(data.safes)) {
            safesList = data.safes;
          } else if (data.address) {
            safesList = [data];
          }
          safesList.forEach(s => {
            if (s.address) {
              safes.push({
                address: s.address,
                chain: s.chain || 'base',
                agentName: agent.name,
                threshold: s.threshold,
                signers: s.signers
              });
            }
          });
        }
      } catch (err) {
        console.log('Failed to load safe for agent', agent.id);
      }
    }
    setKnownSafes(safes);
  };

  const loadSafeData = async () => {
    if (!safeAddress) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Try to get Safe info by address
      // First check if this Safe belongs to any of the user's agents
      let foundSafe = null;
      let foundAgentId = null;
      
      for (const agent of agents) {
        const res = await api.getSafeInfo(token, agent.id);
        if (res.success && res.data) {
          const data = res.data;
          let safesList = [];
          if (Array.isArray(data)) {
            safesList = data;
          } else if (data.safes && Array.isArray(data.safes)) {
            safesList = data.safes;
          } else if (data.address) {
            safesList = [data];
          }
          
          const matchingSafe = safesList.find(
            s => s.address?.toLowerCase() === safeAddress.toLowerCase()
          );
          
          if (matchingSafe) {
            foundSafe = matchingSafe;
            foundAgentId = agent.id;
            break;
          }
        }
      }

      if (foundSafe) {
        setSafeInfo(foundSafe);
        
        // Get pending transactions for this agent's Safe
        const pendingRes = await api.getSafePendingTxs(token, foundAgentId);
        if (pendingRes.success && pendingRes.data?.transactions) {
          // Filter to only transactions for this Safe address
          const txsForSafe = pendingRes.data.transactions.filter(
            tx => tx.safeAddress?.toLowerCase() === safeAddress.toLowerCase() || true // include all if no filter
          );
          setPendingTxs(txsForSafe);
        } else {
          setPendingTxs([]);
        }
      } else {
        // Safe not found in user's agents - show basic info
        setSafeInfo({
          address: safeAddress,
          chain: chain,
          threshold: '?',
          signers: []
        });
        setPendingTxs([]);
        setError('This Safe is not linked to any of your agents. You can still connect your wallet if you are a signer.');
      }
    } catch (err) {
      console.error('Failed to load Safe data:', err);
      setError('Failed to load Safe data');
    }
    setLoading(false);
  };

  const handleLoadSafe = () => {
    if (!safeAddressInput || safeAddressInput.length !== 42 || !safeAddressInput.startsWith('0x')) {
      setError('Please enter a valid Safe address (0x... format, 42 characters)');
      return;
    }
    setSafeAddress(safeAddressInput);
  };

  const handleSelectKnownSafe = (safe) => {
    setSafeAddressInput(safe.address);
    setSafeAddress(safe.address);
    setChain(safe.chain);
  };

  const connectSignerWallet = async () => {
    setConnecting(true);
    try {
      if (!window.ethereum) {
        alert('Please install MetaMask or another Web3 wallet');
        setConnecting(false);
        return;
      }

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      await provider.send('eth_requestAccounts', []);
      const signer = provider.getSigner();
      const address = await signer.getAddress();

      setSignerWallet(signer);
      setSignerAddress(address);
    } catch (err) {
      console.error('Failed to connect wallet:', err);
      alert('Failed to connect wallet: ' + err.message);
    }
    setConnecting(false);
  };

  const disconnectWallet = () => {
    setSignerWallet(null);
    setSignerAddress(null);
  };

  const handleSignTransaction = async (tx) => {
    if (!signerWallet || !signerAddress) {
      alert('Please connect your signer wallet first');
      return;
    }

    // Check if connected wallet is a valid signer
    const isValidSigner = safeInfo?.signers?.some(
      s => s.address?.toLowerCase() === signerAddress.toLowerCase()
    );

    if (!isValidSigner && safeInfo?.signers?.length > 0) {
      alert(`The connected wallet (${signerAddress.slice(0, 8)}...) is not a signer for this Safe.`);
      return;
    }

    setSigningTx(tx.id);
    try {
      // Create the message hash to sign (EIP-712 style for Gnosis Safe)
      const messageHash = tx.safeTxHash || tx.hash;
      
      // Sign the hash with connected wallet
      const signature = await signerWallet.signMessage(ethers.utils.arrayify(messageHash));
      
      // Adjust signature v value for Safe (must be 31 or 32)
      const adjustedSig = adjustSignatureForSafe(signature);

      // Submit confirmation to backend
      const res = await api.confirmSafeTx(token, tx.id, signerAddress, adjustedSig);
      
      if (res.success) {
        alert('Transaction signed successfully!');
        loadSafeData(); // Refresh
      } else {
        alert('Failed to confirm: ' + getErrorMessage(res.error));
      }
    } catch (err) {
      console.error('Signing failed:', err);
      alert('Signing failed: ' + err.message);
    }
    setSigningTx(null);
  };

  const handleRejectTransaction = async (tx) => {
    if (!signerAddress) {
      alert('Please connect your signer wallet first');
      return;
    }

    const reason = prompt('Enter rejection reason (optional):');
    
    setSigningTx(tx.id);
    try {
      const res = await api.rejectSafeTx(token, tx.id, signerAddress, reason || '');
      if (res.success) {
        alert('Transaction rejected');
        loadSafeData();
      } else {
        alert('Failed to reject: ' + getErrorMessage(res.error));
      }
    } catch (err) {
      alert('Failed to reject: ' + err.message);
    }
    setSigningTx(null);
  };

  const handleExecuteTransaction = async (tx) => {
    setSigningTx(tx.id);
    try {
      const res = await api.executeSafeTx(token, tx.id);
      if (res.success) {
        alert('Transaction executed!');
        loadSafeData();
      } else {
        alert('Failed to execute: ' + getErrorMessage(res.error));
      }
    } catch (err) {
      alert('Failed to execute: ' + err.message);
    }
    setSigningTx(null);
  };

  // Check if connected wallet is a signer
  const isConnectedSigner = signerAddress && safeInfo?.signers?.some(
    s => s.address?.toLowerCase() === signerAddress.toLowerCase()
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Safe Transaction Signing</h1>
        <p className="text-white/50 text-sm mt-1">
          Enter a Safe address or select one to view and sign pending transactions
        </p>
      </div>

      {/* Safe Address Input */}
      <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
        <h2 className="text-lg font-semibold text-white mb-4">Select Safe</h2>
        
        {/* Known Safes Quick Select */}
        {knownSafes.length > 0 && (
          <div className="mb-4">
            <p className="text-white/50 text-xs uppercase tracking-wider mb-2">Your Safes</p>
            <div className="flex flex-wrap gap-2">
              {knownSafes.map((safe, i) => (
                <button
                  key={i}
                  onClick={() => handleSelectKnownSafe(safe)}
                  className={`px-3 py-2 rounded-xl text-sm transition-colors ${
                    safeAddress === safe.address
                      ? 'bg-green-500/20 border border-green-500/30 text-green-400'
                      : 'bg-white/[0.03] border border-white/[0.06] text-white/70 hover:bg-white/[0.05]'
                  }`}
                >
                  <span className="font-mono text-xs">{safe.address.slice(0, 8)}...{safe.address.slice(-6)}</span>
                  <span className="text-white/40 ml-2">({safe.agentName})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Manual Address Input */}
        <div className="flex gap-3">
          <div className="flex-1">
            <input
              type="text"
              value={safeAddressInput}
              onChange={(e) => setSafeAddressInput(e.target.value)}
              placeholder="Enter Safe address (0x...)"
              className="w-full px-4 py-3 rounded-xl bg-black border border-white/10 text-white font-mono text-sm outline-none focus:border-white/20 transition-colors"
              data-testid="safe-address-input"
            />
          </div>
          <select
            value={chain}
            onChange={(e) => setChain(e.target.value)}
            className="px-4 py-3 rounded-xl bg-black border border-white/10 text-white outline-none"
          >
            {Object.values(CHAINS).map(c => (
              <option key={c.key} value={c.key}>{c.name}</option>
            ))}
          </select>
          <motion.button
            onClick={handleLoadSafe}
            disabled={loading}
            className="px-5 py-3 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            data-testid="load-safe-btn"
          >
            {loading ? 'Loading...' : 'Load Safe'}
          </motion.button>
        </div>

        {error && !safeInfo?.address && (
          <div className="mt-3 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}
      </div>

      {/* Signer Wallet Connection */}
      <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Signer Wallet</h2>
            <p className="text-white/50 text-sm mt-1">
              Connect the wallet you use to sign Safe transactions
            </p>
          </div>
          
          {signerAddress ? (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-green-500/10 border border-green-500/20">
                <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <code className="text-green-400 font-mono text-sm">
                  {signerAddress.slice(0, 8)}...{signerAddress.slice(-6)}
                </code>
                {isConnectedSigner && (
                  <span className="text-green-400/60 text-xs ml-2">✓ Valid Signer</span>
                )}
              </div>
              <button
                onClick={disconnectWallet}
                className="px-4 py-2 rounded-xl bg-white/[0.05] border border-white/[0.08] text-white/60 text-sm hover:bg-white/[0.08]"
              >
                Disconnect
              </button>
            </div>
          ) : (
            <motion.button
              onClick={connectSignerWallet}
              disabled={connecting}
              className="px-5 py-2.5 rounded-xl bg-white text-black font-medium text-sm disabled:opacity-50"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="connect-signer-btn"
            >
              {connecting ? 'Connecting...' : 'Connect Wallet'}
            </motion.button>
          )}
        </div>

        {signerAddress && !isConnectedSigner && safeInfo?.signers?.length > 0 && (
          <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
            <p className="text-yellow-400 text-sm">
              ⚠️ The connected wallet is not a signer for this Safe. Connect a wallet that is listed as a signer.
            </p>
          </div>
        )}
      </div>

      {/* Safe Info */}
      {safeInfo?.address && (
        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
          <h2 className="text-lg font-semibold text-white mb-4">Safe Details</h2>
          
          {error && (
            <div className="mb-4 p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/20">
              <p className="text-yellow-400 text-sm">{error}</p>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-4 rounded-xl bg-black/30">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Safe Address</p>
              <div className="flex items-center gap-2">
                <code className="text-white font-mono text-sm truncate">{safeInfo.address}</code>
                <button 
                  onClick={() => navigator.clipboard.writeText(safeInfo.address)}
                  className="p-1 text-white/40 hover:text-white/70"
                >
                  <Icons.Copy size={14} />
                </button>
              </div>
            </div>
            <div className="p-4 rounded-xl bg-black/30">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Threshold</p>
              <p className="text-white text-xl font-bold">
                {safeInfo.threshold} {safeInfo.signers?.length ? `of ${safeInfo.signers.length}` : ''}
              </p>
            </div>
            <div className="p-4 rounded-xl bg-black/30">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Chain</p>
              <p className="text-white text-xl font-bold capitalize">{safeInfo.chain || chain}</p>
            </div>
          </div>

          {/* Signers */}
          {safeInfo.signers?.length > 0 && (
            <div>
              <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Signers</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {safeInfo.signers.map((signer, i) => (
                  <div 
                    key={i} 
                    className={`flex items-center justify-between p-3 rounded-xl ${
                      signer.address?.toLowerCase() === signerAddress?.toLowerCase()
                        ? 'bg-green-500/10 border border-green-500/20'
                        : 'bg-black/30'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/60 text-sm font-medium">
                        {i + 1}
                      </div>
                      <code className="text-white/70 font-mono text-sm">
                        {signer.address?.slice(0, 10)}...{signer.address?.slice(-8)}
                      </code>
                    </div>
                    <div className="flex items-center gap-2">
                      {signer.label && (
                        <span className="text-white/40 text-sm">{signer.label}</span>
                      )}
                      {signer.address?.toLowerCase() === signerAddress?.toLowerCase() && (
                        <span className="px-2 py-0.5 rounded text-xs bg-green-500/20 text-green-400">You</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pending Transactions */}
      {safeInfo?.address && (
        <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">
              Pending Transactions
              {pendingTxs.length > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-400 text-sm">
                  {pendingTxs.length}
                </span>
              )}
            </h2>
            <button
              onClick={loadSafeData}
              disabled={loading}
              className="px-3 py-1.5 rounded-lg bg-white/[0.05] text-white/60 text-sm hover:bg-white/[0.08] disabled:opacity-50"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>

          {pendingTxs.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-white/[0.03] flex items-center justify-center mx-auto mb-4">
                <Icons.Check size={32} className="text-white/20" />
              </div>
              <p className="text-white/50">No pending transactions</p>
              <p className="text-white/30 text-sm mt-1">All transactions have been processed</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingTxs.map((tx) => (
                <TransactionCard
                  key={tx.id}
                  tx={tx}
                  safe={safeInfo}
                  signerAddress={signerAddress}
                  isConnectedSigner={isConnectedSigner}
                  signingTx={signingTx}
                  onSign={() => handleSignTransaction(tx)}
                  onReject={() => handleRejectTransaction(tx)}
                  onExecute={() => handleExecuteTransaction(tx)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TransactionCard({ tx, safe, signerAddress, isConnectedSigner, signingTx, onSign, onReject, onExecute }) {
  const [expanded, setExpanded] = useState(false);
  const status = STATUS_COLORS[tx.status] || STATUS_COLORS.PROPOSED;
  
  const confirmations = tx.confirmations || [];
  const threshold = safe?.threshold || 2;
  const confirmedCount = confirmations.length;
  const canExecute = confirmedCount >= threshold;
  
  // Check if current signer has already signed
  const hasAlreadySigned = signerAddress && confirmations.some(
    c => c.signer?.toLowerCase() === signerAddress?.toLowerCase()
  );

  const isProcessing = signingTx === tx.id;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-xl border ${status.border} ${status.bg}`}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-black/30 flex items-center justify-center">
            <span className="text-white/60 text-sm font-mono">#{tx.nonce || '?'}</span>
          </div>
          <div>
            <p className="text-white font-medium">
              {tx.description || tx.plugin || 'Transaction'}
            </p>
            <p className="text-white/40 text-sm">
              To: {tx.to?.slice(0, 10)}...{tx.to?.slice(-8)}
              {tx.value && tx.value !== '0' && (
                <span className="ml-2">• {ethers.utils.formatEther(tx.value)} ETH</span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Confirmation Progress */}
          <div className="flex items-center gap-2">
            <div className="flex -space-x-1">
              {[...Array(Math.max(threshold, confirmedCount))].map((_, i) => (
                <div
                  key={i}
                  className={`w-6 h-6 rounded-full border-2 border-black flex items-center justify-center text-xs ${
                    i < confirmedCount
                      ? 'bg-green-500 text-white'
                      : 'bg-white/10 text-white/30'
                  }`}
                >
                  {i < confirmedCount ? '✓' : i + 1}
                </div>
              ))}
            </div>
            <span className="text-white/50 text-sm">{confirmedCount}/{threshold}</span>
          </div>

          <span className={`px-2 py-1 rounded text-xs ${status.bg} ${status.text}`}>
            {status.label}
          </span>

          <svg 
            className={`w-5 h-5 text-white/40 transition-transform ${expanded ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      {/* Expanded Details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pt-4 mt-4 border-t border-white/[0.06] space-y-4">
              {/* Transaction Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-white/40 text-xs uppercase mb-1">To Address</p>
                  <code className="text-white/70 font-mono break-all">{tx.to}</code>
                </div>
                <div>
                  <p className="text-white/40 text-xs uppercase mb-1">Value</p>
                  <p className="text-white">{tx.value ? ethers.utils.formatEther(tx.value) : '0'} ETH</p>
                </div>
                {tx.safeTxHash && (
                  <div className="col-span-2">
                    <p className="text-white/40 text-xs uppercase mb-1">Safe Tx Hash</p>
                    <code className="text-white/50 font-mono text-xs break-all">{tx.safeTxHash}</code>
                  </div>
                )}
              </div>

              {/* Confirmations */}
              <div>
                <p className="text-white/40 text-xs uppercase mb-2">Confirmations ({confirmedCount}/{threshold})</p>
                <div className="space-y-2">
                  {confirmations.map((conf, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-green-500/10">
                      <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center text-white text-xs">✓</div>
                      <code className="text-green-400 font-mono text-sm">
                        {conf.signer?.slice(0, 10)}...{conf.signer?.slice(-8)}
                      </code>
                      {conf.signer?.toLowerCase() === signerAddress?.toLowerCase() && (
                        <span className="text-green-400/60 text-xs">(You)</span>
                      )}
                    </div>
                  ))}
                  {confirmations.length === 0 && (
                    <p className="text-white/30 text-sm">No confirmations yet</p>
                  )}
                </div>
              </div>

              {/* Actions */}
              {(tx.status === 'PROPOSED' || tx.status === 'PENDING') && (
                <div className="flex items-center gap-3 pt-2">
                  {!hasAlreadySigned && isConnectedSigner && (
                    <motion.button
                      onClick={(e) => { e.stopPropagation(); onSign(); }}
                      disabled={isProcessing}
                      className="flex-1 px-4 py-2.5 rounded-xl bg-green-500 text-white font-medium text-sm disabled:opacity-50"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {isProcessing ? 'Signing...' : '✓ Sign & Approve'}
                    </motion.button>
                  )}
                  {hasAlreadySigned && (
                    <div className="flex-1 px-4 py-2.5 rounded-xl bg-green-500/20 text-green-400 font-medium text-sm text-center">
                      ✓ You've signed this transaction
                    </div>
                  )}
                  {canExecute && (
                    <motion.button
                      onClick={(e) => { e.stopPropagation(); onExecute(); }}
                      disabled={isProcessing}
                      className="px-4 py-2.5 rounded-xl bg-blue-500 text-white font-medium text-sm disabled:opacity-50"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {isProcessing ? 'Executing...' : 'Execute'}
                    </motion.button>
                  )}
                  {isConnectedSigner && (
                    <motion.button
                      onClick={(e) => { e.stopPropagation(); onReject(); }}
                      disabled={isProcessing}
                      className="px-4 py-2.5 rounded-xl bg-red-500/20 text-red-400 font-medium text-sm disabled:opacity-50"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Reject
                    </motion.button>
                  )}
                  {!isConnectedSigner && !signerAddress && (
                    <p className="text-yellow-400 text-sm">Connect your signer wallet to take action</p>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
